import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Environment, ENVIRONMENT } from '@netfoundry-ui/shared/model';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HTTP_CLIENT, TokenService } from './token.service';

// IpsecTunnelerReportingService
//
// Calls the generic /reporting/v1/template-search/{templateName} endpoint with the
// three mustache templates installed via reporting-templates/terraform/ipsec_tunneler.tf:
//   ipsec_tunneler_bridge_health_tmpl     — pill + IPsec snapshot (one round-trip; 90s
//                                            live aggregation + 7d backfill max(@timestamp))
//   ipsec_tunneler_metrics_timeseries_tmpl — Metrics tab (1h/24h/7d windowed series)
//   ipsec_tunneler_events_tmpl             — Events tab (charon syslog)
//
// All templates filter on the same identity tuple: networkId, networkGroupId,
// ipsec_tunneler_id (mixed case in the metricbeat docs; do not snake-case
// networkId/networkGroupId).  Auth happens at the controller via
// AuthorizationService.isAuthorizedToReadElasticSearchOnNetwork — the caller asserts
// the network in the params and the server confirms the bearer has access.
//
// The CompositeHealth classifier is implemented in-line below; the older server-side
// classifier was removed when the bespoke /ipsec-tunnelers endpoints were retired in
// favor of the template-search pattern (2026-05-26).

// ---- Wire types ----

export type CompositeHealth = 'HEALTHY' | 'DEGRADED' | 'DOWN' | 'STALE' | 'PROVISIONING';

export interface ChildSaSnapshotDto {
    childId: string;
    childName: string | null;
    state: string | null;
    establishedAt: number | null;
    rekeyAt: number | null;
    lastInboundSeconds: number | null;
    lastOutboundSeconds: number | null;
    bytesIn: number;
    bytesOut: number;
    localTrafficSelectors: string[];
    remoteTrafficSelectors: string[];
}

// compositeHealth carries every state, liveness included:
//   PROVISIONING = no docs in 7d
//   STALE        = docs in 7d but none in 90s, OR docs in 90s with scrape_up != 1
//   HEALTHY      = Phase 1 up + ≥1 INSTALLED CHILD_SA inside its rekey window
//   DEGRADED     = IKE not established, OR every INSTALLED CHILD_SA past its rekey deadline
//   DOWN         = Phase 1 up but no INSTALLED CHILD_SA at all — no data path
export interface BridgeHealthDto {
    compositeHealth: CompositeHealth;
    lastSeenAt: number | null;
    ikeState: string | null;
    ikeEstablishedAt: number | null;
    ikesConnected: number;
    exporterScrapeUp: boolean;
    cpuPct: number | null;
    memPct: number | null;
    childSAs: ChildSaSnapshotDto[];
}

/** Whether a live IPsec signal is up, down, or not currently reported. */
export type IpsecPhaseState = 'up' | 'down' | 'unknown';

/**
 * Phase 1 as a three-way signal. A provisioning or stale snapshot reads `unknown` rather than down, as the bridge may
 * be up and merely unheard from.
 */
export function ipsecIkeState(health: BridgeHealthDto | null | undefined): IpsecPhaseState {
    if (!hasLiveSnapshot(health)) {
        return 'unknown';
    }
    return health.ikeState === 'ESTABLISHED' && health.ikesConnected > 0 ? 'up' : 'down';
}

/**
 * Phase 2 as the same three-way signal. Up means at least one INSTALLED CHILD_SA, which is what carries traffic; a live
 * IKE_SA alone moves no packets.
 */
export function ipsecChildSaState(health: BridgeHealthDto | null | undefined): IpsecPhaseState {
    if (!hasLiveSnapshot(health)) {
        return 'unknown';
    }
    return health.childSAs.some((sa) => sa.state === 'INSTALLED') ? 'up' : 'down';
}

/** How many CHILD_SAs are installed, for a caller that names the number beside the signal. */
export function ipsecInstalledChildSaCount(health: BridgeHealthDto | null | undefined): number {
    return (health?.childSAs ?? []).filter((sa) => sa.state === 'INSTALLED').length;
}

// PROVISIONING means nothing has ever been reported and STALE nothing inside the 90s window, so
// neither says anything about IKE or the CHILD_SAs.
function hasLiveSnapshot(health: BridgeHealthDto | null | undefined): health is BridgeHealthDto {
    return !!health && health.compositeHealth !== 'PROVISIONING' && health.compositeHealth !== 'STALE';
}

// All three series widen rx/tx/cpuPct/memPct/v to `number | null` so the
// chart can render true gaps for buckets where the aggregation produces
// no value — e.g. derivative of a counter at SA rekey (negative-then-clamped
// upstream became a misleading flat-zero), or an empty date_histogram bucket
// with `min_doc_count: 0`.
export interface RxTxPoint {
    t: number;
    rx: number | null;
    tx: number | null;
}

export interface SystemPoint {
    t: number;
    cpuPct: number | null;
    memPct: number | null;
}

export interface ScalarPoint {
    t: number;
    v: number | null;
}

// Per-bucket IKE/CHILD-SA state used to classify null rx/tx gaps. If `ikeUp` stays true
// across a stretch of null rx/tx buckets the gap is a CHILD-SA rekey (data plane was
// fine, the byte counter just reset). A run of `ikeUp = false` buckets is a real outage
// — see TunnelDowntimeRange below.
//
// `perChildEstablishedSec` is the FRESHEST `strongswan_sa_established_second` seen in
// the bucket (the metric is **elapsed seconds since the CHILD_SA came up**, not a
// timestamp — it grows monotonically while an SA persists and resets to ~0 on rekey).
// A child whose value DECREASES between consecutive buckets has just re-established;
// several children's values resetting together (right after an outage edge) is the full
// IKE-rebuild signature.
export interface TunnelStatePoint {
    t: number;
    ikeUp: boolean | null;
    ikesConnected: number | null;
    perChildEstablishedSec: Record<string, number | null>;
}

// Contiguous run of buckets where IKE was DOWN (ikeUp = false). Computed by the parser
// so the chart can drop these straight into [downtimeRanges] without re-walking the
// series. start/end are bucket-key timestamps (ms); end is INCLUSIVE of the last DOWN
// bucket — the chart's downtime renderer handles the closed-interval semantics.
export interface TunnelDowntimeRange {
    start: number;
    end: number;
}

// Point-in-time CHILD_SA rekey event derived by walking consecutive tunnelState buckets:
// a child whose `perChildEstablishedSec` value DECREASES from one bucket to the next has
// just re-established (the strongswan elapsed-seconds counter reset to 0 on rekey).
// `childIds` carries every child that rekeyed in that bucket so the tooltip / future
// drill-down can name them; multiple children rekeying in the same bucket (typically
// right at an outage-recovery edge) is the full-IKE-rebuild signature.
export interface RekeyEvent {
    t: number;
    childIds: string[];
}

export type MetricsWindow = '1h' | '24h' | '7d';

export interface IpsecTunnelerMetricsDto {
    window: MetricsWindow;
    interval: string;
    generatedAt: number;
    series: {
        rxTx: RxTxPoint[];
        system: SystemPoint[];
        lastInboundSeconds: ScalarPoint[];
        lastOutboundSeconds: ScalarPoint[];
        tunnelState: TunnelStatePoint[];
    };
    downtimeRanges: TunnelDowntimeRange[];
    rekeyEvents: RekeyEvent[];
}

export type EventSeverity = 'INFO' | 'WARN' | 'ERROR';
// Kind is left as a free-form string. The filebeat sources we currently collect
// (kern.log + ziti-router journald) don't carry a normalized kind; we derive
// a coarse one client-side from log.syslog.appname / log.file.path / tags.
export type EventKind = string;

export interface IpsecTunnelerEventDto {
    t: number;
    severity: EventSeverity;
    kind: EventKind;
    message: string;
}

// ---- Template + index constants ----

const TMPL_BRIDGE_HEALTH = 'ipsec_tunneler_bridge_health_tmpl';
const TMPL_METRICS = 'ipsec_tunneler_metrics_timeseries_tmpl';
const TMPL_EVENTS = 'ipsec_tunneler_events_tmpl';

const INDEX_METRICBEAT = 'ncipsecmetrics';
const INDEX_FILEBEAT = 'ncipseclogs';

// Window → (spanMs, ES fixed_interval string) lookup for the Metrics tab.
const METRICS_WINDOW_CONF: Record<MetricsWindow, { spanMs: number; interval: string }> = {
    '1h': { spanMs: 60 * 60_000, interval: '30s' },
    '24h': { spanMs: 24 * 60 * 60_000, interval: '1m' },
    '7d': { spanMs: 7 * 24 * 60 * 60_000, interval: '15m' },
};

const EVENT_SEVERITY_ORDER: Record<EventSeverity, number> = { INFO: 0, WARN: 1, ERROR: 2 };

// Grace on the CHILD_SA rekey deadline before a tunnel counts as DEGRADED. The template's
// `max` on `sa_rekey_second` already biases `rekeyAt` later, so this absorbs scrape jitter only.
const REKEY_OVERDUE_GRACE_MS = 120_000;

// ---- Service ----

@Injectable({ providedIn: 'root' })
export class IpsecTunnelerReportingService {
    // Set to true to render the mock data (offline UI dev). The mock paths exercise
    // the three composite-health states via tunnelerId suffix: ids ending in
    // '-never' → PROVISIONING, '-stale' → STALE, anything else → HEALTHY.
    public useMock = false;

    private readonly templateUrl: string;

    constructor(
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment,
        private tokenService: TokenService
    ) {
        this.templateUrl = this.environment.elasticConfig.reportingTemplateServiceUrl;
    }

    // Single-tunneler convenience wrapper around getBridgeHealthForTunnelers — kept so the
    // detail page (and any other one-id caller) doesn't have to unwrap a Map of size 1.
    // Returns a "never reported" PROVISIONING DTO when the tunneler has no ES docs yet,
    // matching the previous single-id semantics.
    getBridgeHealth(tunnelerId: string, networkId: string, networkGroupId: string): Observable<BridgeHealthDto> {
        if (this.useMock) {
            return of(this.mockBridgeHealth(tunnelerId));
        }
        return this.getBridgeHealthForTunnelers([tunnelerId], networkId, networkGroupId).pipe(
            map((m) => m.get(tunnelerId) ?? this.parseBridgeHealthBucket({}))
        );
    }

    // Fetch bridge health for many tunnelers in one ES round-trip. The list page calls
    // this with every visible tunneler's id so the Name-column status ball reflects the
    // live composite health rather than the entity-status field. Returns a Map keyed by
    // tunneler id; ids with no matching ES docs are simply absent from the map (caller
    // should treat absence as PROVISIONING / never-reported).
    getBridgeHealthForTunnelers(
        ipsecTunnelerIds: string[],
        networkId: string,
        networkGroupId: string
    ): Observable<Map<string, BridgeHealthDto>> {
        if (this.useMock) {
            const m = new Map<string, BridgeHealthDto>();
            for (const id of ipsecTunnelerIds) m.set(id, this.mockBridgeHealth(id));
            return of(m);
        }
        if (!ipsecTunnelerIds || ipsecTunnelerIds.length === 0) {
            return of(new Map());
        }
        // The template emits {{&ipsecTunnelerIds}} verbatim into the `terms` filter, so we
        // send a pre-formatted JSON array string. Sticking with HttpParams (scalar string)
        // sidesteps any list-binding behavior in the template-search proxy.
        const params = new HttpParams()
            .set('networkId', networkId)
            .set('networkGroupId', networkGroupId)
            .set('ipsecTunnelerIds', JSON.stringify(ipsecTunnelerIds))
            .set('indexName', INDEX_METRICBEAT);
        return this.http
            .get<any>(`${this.templateUrl}/${TMPL_BRIDGE_HEALTH}`, { headers: this.headers(), params })
            .pipe(map((resp) => this.parseBridgeHealthByTunneler(resp)));
    }

    getMetrics(
        tunnelerId: string,
        networkId: string,
        networkGroupId: string,
        window: MetricsWindow = '1h'
    ): Observable<IpsecTunnelerMetricsDto> {
        if (this.useMock) {
            return of(this.mockMetrics(window));
        }
        const conf = METRICS_WINDOW_CONF[window];
        const now = Date.now();
        const params = this.identityParams(tunnelerId, networkId, networkGroupId, INDEX_METRICBEAT)
            .set('gte', String(now - conf.spanMs))
            .set('lte', String(now))
            .set('interval', conf.interval);
        return this.http
            .get<any>(`${this.templateUrl}/${TMPL_METRICS}`, { headers: this.headers(), params })
            .pipe(map((resp) => this.parseMetrics(window, conf.interval, resp)));
    }

    // gteMs / lteMs are absolute epoch milliseconds; the caller (component) is
    // responsible for translating quick-window buttons or a custom datetime-local
    // pair into this absolute pair. Keeps the service free of time-mode logic.
    // `kind`, when non-null, is forwarded to the template as a match_phrase filter on
    // log.syslog.appname.keyword (server-side filter). Pass null to fetch everything.
    // `search`, when non-null, restricts to messages containing that word or phrase.
    getEvents(
        tunnelerId: string,
        networkId: string,
        networkGroupId: string,
        gteMs: number,
        lteMs: number,
        kind: string | null = null,
        search: string | null = null,
        size = 500
    ): Observable<IpsecTunnelerEventDto[]> {
        if (this.useMock) {
            return of(this.mockEvents(kind));
        }
        let params = this.identityParams(tunnelerId, networkId, networkGroupId, INDEX_FILEBEAT)
            .set('gte', String(gteMs))
            .set('lte', String(lteMs))
            .set('size', String(size));
        if (kind) {
            params = params.set('kind', kind);
        }
        // Forwarded to the template as a match_phrase on `message`. Server-side because the
        // response is capped at `size`; filtering here would only search the page we received.
        if (search) {
            params = params.set('search', search);
        }
        return this.http
            .get<any>(`${this.templateUrl}/${TMPL_EVENTS}`, { headers: this.headers(), params })
            .pipe(map((resp) => this.parseEvents(resp)));
    }

    // ---- Parsers (ES response → DTO) ----

    // Walk the outer `by_tunneler` terms aggregation. Each bucket already carries
    // max_ts_7d + snapshot_90s as direct keys (same shape as the old single-id response's
    // top-level aggregations), so parseBridgeHealthBucket consumes a bucket directly.
    private parseBridgeHealthByTunneler(esResp: any): Map<string, BridgeHealthDto> {
        const out = new Map<string, BridgeHealthDto>();
        const buckets: any[] = esResp?.aggregations?.by_tunneler?.buckets ?? [];
        for (const bucket of buckets) {
            const id = bucket?.key;
            if (typeof id !== 'string' || id.length === 0) continue;
            out.set(id, this.parseBridgeHealthBucket(bucket));
        }
        return out;
    }

    // Per-bucket health derivation. The argument is the aggs object (a `by_tunneler`
    // bucket, or an empty {} for ids absent from the response — yields PROVISIONING).
    private parseBridgeHealthBucket(aggs: any): BridgeHealthDto {
        const maxTs7d: number | null = aggs?.max_ts_7d?.value ?? null;
        const snap = aggs?.snapshot_90s;
        const snapHasDocs = (snap?.doc_count ?? 0) > 0;

        const ikeStateRaw = snap?.ike_state?.value;
        const ikeEstablishedSec: number | null = snap?.ike_established_second?.value ?? null;
        const ikesConnected = Math.round(snap?.ikes_connected?.value ?? 0);
        const scrapeUpRaw: number | null = snap?.scrape_up?.value ?? null;
        const cpuPctNorm: number | null = snap?.cpu_pct?.value ?? null;
        const memPctNorm: number | null = snap?.mem_pct?.value ?? null;
        const lastSeen: number | null = snap?.last_seen?.value ?? null;

        const exporterScrapeUp = scrapeUpRaw != null && scrapeUpRaw >= 1.0;
        const ikeState = this.ikeStateLabel(ikeStateRaw);

        // The LarsTi exporter exposes `strongswan_*_established_second` as ELAPSED seconds since
        // the SA came up (matches strongSwan VICI `install-time`), not a Unix timestamp. Convert
        // to wall-clock by anchoring against the freshest scrape we saw (lastSeen, ms). Without
        // the anchor, multiplying elapsed-seconds by 1000 produces ~1970 dates in the UI.
        const lastSeenForAnchor = lastSeen ?? maxTs7d;
        const childSAs = this.parseChildSAs(snap?.by_child?.buckets ?? [], lastSeenForAnchor);

        let compositeHealth: CompositeHealth;
        if (!snapHasDocs && maxTs7d == null) {
            compositeHealth = 'PROVISIONING';
        } else if (!snapHasDocs) {
            compositeHealth = 'STALE';
        } else if (!exporterScrapeUp) {
            compositeHealth = 'STALE';
        } else if (ikeState === 'ESTABLISHED' && ikesConnected > 0) {
            compositeHealth = this.classifyPhase2(childSAs, lastSeen);
        } else {
            compositeHealth = 'DEGRADED';
        }

        return {
            compositeHealth,
            lastSeenAt: lastSeen ?? maxTs7d,
            ikeState,
            ikeEstablishedAt:
                ikeEstablishedSec != null && lastSeenForAnchor != null
                    ? Math.round(lastSeenForAnchor - ikeEstablishedSec * 1000)
                    : null,
            ikesConnected,
            exporterScrapeUp,
            cpuPct: cpuPctNorm != null ? cpuPctNorm * 100 : null,
            memPct: memPctNorm != null ? memPctNorm * 100 : null,
            childSAs,
        };
    }

    // Phase 2 gate for a tunnel whose Phase 1 is up. A live IKE_SA does not imply a data path:
    // a peer that drops its child config keeps answering IKE while every CHILD_SA rekey fails.
    // `sa_rekey_second` is a countdown, so a `rekeyAt` in the past means the rekey keeps failing
    // and the SA dies at its hard lifetime. A null `lastSeen` gives no deadline to compare.
    private classifyPhase2(childSAs: ChildSaSnapshotDto[], lastSeen: number | null): CompositeHealth {
        const installed = childSAs.filter((c) => c.state === 'INSTALLED');
        if (installed.length === 0) return 'DOWN';
        if (lastSeen == null) return 'HEALTHY';
        const deadline = lastSeen - REKEY_OVERDUE_GRACE_MS;
        const allOverdue = installed.every((c) => c.rekeyAt != null && c.rekeyAt < deadline);
        return allOverdue ? 'DEGRADED' : 'HEALTHY';
    }

    // `sa_established_second` is elapsed seconds since the CHILD_SA came up (VICI install-time);
    // `sa_rekey_second` is seconds until the next rekey (VICI rekey-time, a countdown). Both are
    // relative to the scrape that produced the value, so wall-clock time is derived from
    // lastSeenMs (the freshest scrape timestamp in the snapshot window): established = anchor -
    // elapsed; rekey = anchor + countdown.
    private parseChildSAs(buckets: any[], lastSeenMs: number | null): ChildSaSnapshotDto[] {
        return buckets.map((b) => {
            // localTS / remoteTS are only emitted on the strongswan_sa_state metric document,
            // not on the byte / packet / timer counter docs in the same bucket. The template's
            // `labels` agg pre-filters to docs that carry localTS so this lookup is stable;
            // an unfiltered top_hits would pick an arbitrary doc per request and the TS
            // columns would intermittently disappear.
            const labels = b?.labels?.hit?.hits?.hits?.[0]?._source?.prometheus?.labels ?? {};
            const saEstabSec = b?.sa_established_second?.value;
            const saRekeySec = b?.sa_rekey_second?.value;
            return {
                childId: String(b.key),
                childName: labels.child_name ?? null,
                state: this.saStateLabel(b?.sa_state?.value),
                establishedAt:
                    saEstabSec != null && lastSeenMs != null ? Math.round(lastSeenMs - saEstabSec * 1000) : null,
                rekeyAt: saRekeySec != null && lastSeenMs != null ? Math.round(lastSeenMs + saRekeySec * 1000) : null,
                // last_inbound_seconds / last_outbound_seconds are wrapped in a
                // filter sub-aggregation (`bytes_*bound > 0`) on the ES side so
                // SAs that have never moved a packet — which the LarsTi exporter
                // reports as `last_*bound_seconds = 0` rather than omitting —
                // don't drag the value to a phantom-fresh 0. Accessor is
                // `.v.value` instead of `.value`; null means "no SA in this row
                // has had traffic yet" which the UI should render as "—".
                lastInboundSeconds: b?.last_inbound_seconds?.v?.value ?? null,
                lastOutboundSeconds: b?.last_outbound_seconds?.v?.value ?? null,
                bytesIn: b?.bytes_inbound?.value ?? 0,
                bytesOut: b?.bytes_outbound?.value ?? 0,
                localTrafficSelectors: this.splitTs(labels.localTS),
                remoteTrafficSelectors: this.splitTs(labels.remoteTS),
            };
        });
    }

    private parseMetrics(window: MetricsWindow, interval: string, esResp: any): IpsecTunnelerMetricsDto {
        const buckets: any[] = esResp?.aggregations?.time_buckets?.buckets ?? [];
        const rxTx: RxTxPoint[] = [];
        const system: SystemPoint[] = [];
        const lastInboundSeconds: ScalarPoint[] = [];
        const lastOutboundSeconds: ScalarPoint[] = [];
        const tunnelState: TunnelStatePoint[] = [];
        for (const b of buckets) {
            const t: number = b.key;
            // rx_rate / tx_rate are derivative outputs over per-child byte counters.
            // Counters reset on strongSwan SA rekey, producing negative deltas; those
            // get emitted as null so the chart draws a gap rather than a misleading
            // flat-zero. Same treatment for missing/null buckets (e.g. first bucket
            // of derivative, or an empty histogram bucket with min_doc_count: 0).
            const rxRate: number | null = b.rx_rate?.value ?? null;
            const txRate: number | null = b.tx_rate?.value ?? null;
            rxTx.push({
                t,
                rx: rxRate != null && rxRate >= 0 ? rxRate : null,
                tx: txRate != null && txRate >= 0 ? txRate : null,
            });
            const cpuRaw: number | null = b.cpu_pct?.value ?? null;
            const memRaw: number | null = b.mem_pct?.value ?? null;
            // metricbeat's system module scrapes every 60s. With a finer histogram
            // interval (rxTx wants 30s for derivative resolution) every other bucket
            // is empty for cpu/mem and the line ends up with no two consecutive
            // defined points to draw between. Drop the empty buckets so the line
            // connects across the natural sampling gaps.
            if (cpuRaw != null || memRaw != null) {
                system.push({
                    t,
                    cpuPct: cpuRaw != null ? cpuRaw * 100 : null,
                    memPct: memRaw != null ? memRaw * 100 : null,
                });
            }
            // Filter sub-agg wraps the `min`, accessor is `.v.value`. See
            // parseChildSAs() above for why — same SA-with-zero-traffic
            // phantom-fresh problem applies here.
            lastInboundSeconds.push({ t, v: b.last_inbound_seconds?.v?.value ?? null });
            lastOutboundSeconds.push({ t, v: b.last_outbound_seconds?.v?.value ?? null });

            // Per-bucket IKE-SA state — drives the rekey-vs-outage discriminator in the
            // chart. ike_state value is 1.0 (ESTABLISHED) or 0 (anything else); we map
            // to a strict boolean and let null pass through as "no data this bucket".
            const ikeStateRaw: number | null = b.ike_state?.value ?? null;
            const ikesConnectedRaw: number | null = b.ikes_connected?.value ?? null;
            const perChildEstablishedSec: Record<string, number | null> = {};
            const childBuckets: any[] = b?.by_child?.buckets ?? [];
            for (const cb of childBuckets) {
                const childId = String(cb?.key ?? '');
                if (!childId) continue;
                // sa_established_min is the freshest "seconds since established" seen
                // in the bucket. While the SA persists this grows by roughly the bucket
                // interval each step; a DECREASE between consecutive buckets is the
                // rekey signature (the strongswan counter reset to 0 when a new SA came
                // up). Picking `min` rather than `max` means a rekey that happens partway
                // through a bucket is still detected (the post-rekey scrape contributes
                // the smallest value, which the next bucket's min must exceed without a
                // further rekey).
                perChildEstablishedSec[childId] = cb?.sa_established_min?.value ?? null;
            }
            tunnelState.push({
                t,
                ikeUp: ikeStateRaw != null ? ikeStateRaw >= 1 : null,
                ikesConnected: ikesConnectedRaw != null ? Math.round(ikesConnectedRaw) : null,
                perChildEstablishedSec,
            });
        }
        return {
            window,
            interval,
            generatedAt: Date.now(),
            series: { rxTx, system, lastInboundSeconds, lastOutboundSeconds, tunnelState },
            downtimeRanges: this.deriveDowntimeRanges(tunnelState),
            rekeyEvents: this.deriveRekeyEvents(tunnelState),
        };
    }

    // Walk consecutive tunnelState buckets and emit a rekey event for any child whose
    // `sa_established_second` (elapsed seconds since the CHILD_SA came up) DECREASED
    // between two buckets — the strongswan counter resets to 0 on rekey, so a drop is
    // unambiguous. A child appearing for the first time after being absent in the prior
    // bucket also counts (covers the post-outage rebuild). Children that disappear are
    // ignored (that's a teardown, not a rekey).
    //
    // The first bucket has no predecessor and never emits. Multiple children resetting
    // in the same bucket collapse to a single event with a multi-child childIds list —
    // the chart still draws one vertical marker, the tooltip distinguishes single rekey
    // from full IKE-rebuild.
    private deriveRekeyEvents(tunnelState: TunnelStatePoint[]): RekeyEvent[] {
        const events: RekeyEvent[] = [];
        for (let i = 1; i < tunnelState.length; i++) {
            const prev = tunnelState[i - 1].perChildEstablishedSec;
            const curr = tunnelState[i].perChildEstablishedSec;
            const rekeyed: string[] = [];
            for (const childId of Object.keys(curr)) {
                const currSec = curr[childId];
                if (currSec == null) continue;
                const prevSec = prev[childId];
                if (prevSec == null || currSec < prevSec) {
                    rekeyed.push(childId);
                }
            }
            if (rekeyed.length > 0) {
                events.push({ t: tunnelState[i].t, childIds: rekeyed });
            }
        }
        return events;
    }

    // Collapse contiguous runs of `ikeUp = false` into TunnelDowntimeRange entries so the
    // chart can paint a single red band per outage rather than per-bucket. `ikeUp = null`
    // (no scrape) does NOT extend a downtime — telemetry-gap and outage are different and
    // the chart should not falsely accuse a tunnel that just stopped reporting. If a
    // future UI wants to surface the unknown-state gaps too, expose a second derived
    // series rather than overloading the downtime semantics.
    private deriveDowntimeRanges(tunnelState: TunnelStatePoint[]): TunnelDowntimeRange[] {
        const ranges: TunnelDowntimeRange[] = [];
        let runStart: number | null = null;
        let runEnd: number | null = null;
        for (const p of tunnelState) {
            if (p.ikeUp === false) {
                if (runStart == null) runStart = p.t;
                runEnd = p.t;
            } else if (runStart != null) {
                ranges.push({ start: runStart, end: runEnd ?? runStart });
                runStart = null;
                runEnd = null;
            }
        }
        if (runStart != null) {
            ranges.push({ start: runStart, end: runEnd ?? runStart });
        }
        return ranges;
    }

    private parseEvents(esResp: any): IpsecTunnelerEventDto[] {
        const hits: any[] = esResp?.hits?.hits ?? [];
        return hits.map((h) => {
            const src = h._source ?? {};
            const tRaw = src['@timestamp'];
            const t = typeof tRaw === 'string' ? Date.parse(tRaw) : Number(tRaw ?? 0);
            return {
                t,
                severity: this.deriveSeverity(src),
                kind: this.deriveKind(src),
                message: src.message ?? '',
            };
        });
    }

    // Map syslog priority (RFC 5424) to the UI's three-bucket severity.
    // Priority absent → default INFO (kern.log audit events have no syslog priority).
    private deriveSeverity(src: any): EventSeverity {
        const p = src?.log?.syslog?.priority;
        if (typeof p !== 'number') return 'INFO';
        if (p <= 3) return 'ERROR'; // emerg/alert/crit/err
        if (p === 4) return 'WARN';
        return 'INFO'; // notice/info/debug
    }

    // Best-effort kind classification. The filebeat sources we collect don't have a
    // structured kind field; we infer one from the most distinguishing field that's
    // present. Falls back to "log" so the chip cell is never blank.
    private deriveKind(src: any): string {
        const appname = src?.log?.syslog?.appname;
        if (typeof appname === 'string' && appname.length > 0) return appname;
        const path: string | undefined = src?.log?.file?.path;
        if (typeof path === 'string') {
            const base = path.split('/').pop() ?? path;
            if (base === 'kern.log') return 'kernel-audit';
            if (base) return base;
        }
        const tags: string[] = Array.isArray(src?.tags) ? src.tags : [];
        if (tags.includes('ziti-router')) return 'ziti-router';
        return 'log';
    }

    // ---- Helpers ----

    private identityParams(
        tunnelerId: string,
        networkId: string,
        networkGroupId: string,
        indexName: string
    ): HttpParams {
        return new HttpParams()
            .set('networkId', networkId)
            .set('networkGroupId', networkGroupId)
            .set('ipsecTunnelerId', tunnelerId)
            .set('indexName', indexName);
    }

    private headers(): HttpHeaders {
        const t = this.tokenService.getAccessToken();
        let h = new HttpHeaders().set('Accept', 'application/json');
        if (t && !t.expired && t.accessToken) {
            h = h.set('Authorization', `Bearer ${t.accessToken}`);
        }
        return h;
    }

    // strongswan_ike_state: 1 = ESTABLISHED, anything else (including null) treated as DOWN.
    private ikeStateLabel(v: number | null | undefined): string | null {
        if (v == null) return null;
        return v >= 1 ? 'ESTABLISHED' : 'DOWN';
    }

    // strongswan_sa_state: 1 = INSTALLED, anything else treated as DOWN.
    private saStateLabel(v: number | null | undefined): string | null {
        if (v == null) return null;
        return v >= 1 ? 'INSTALLED' : 'DOWN';
    }

    // Traffic-selector label is a semicolon-separated string in the metricbeat doc
    // (e.g. "10.19.143.214/32;10.19.143.215/32"). Empty/undefined → empty array.
    private splitTs(s: string | null | undefined): string[] {
        if (!s) return [];
        return s.split(';').filter((p) => p.length > 0);
    }

    // ---- Mock backend (offline UI dev only; gated on useMock) ----

    private mockBridgeHealth(id: string): BridgeHealthDto {
        const now = Date.now();
        if (id.endsWith('-never')) {
            return {
                compositeHealth: 'PROVISIONING',
                lastSeenAt: null,
                ikeState: null,
                ikeEstablishedAt: null,
                ikesConnected: 0,
                exporterScrapeUp: false,
                cpuPct: null,
                memPct: null,
                childSAs: [],
            };
        }
        if (id.endsWith('-stale')) {
            return {
                compositeHealth: 'STALE',
                lastSeenAt: now - 4 * 60 * 1000 - 12 * 1000,
                ikeState: 'ESTABLISHED',
                ikeEstablishedAt: now - 6 * 60 * 60 * 1000,
                ikesConnected: 1,
                exporterScrapeUp: false,
                cpuPct: 8.4,
                memPct: 41.2,
                childSAs: [this.mockChildSa(now, '-stale')],
            };
        }
        return {
            compositeHealth: 'HEALTHY',
            lastSeenAt: now - 12 * 1000,
            ikeState: 'ESTABLISHED',
            ikeEstablishedAt: now - 6 * 60 * 60 * 1000,
            ikesConnected: 1,
            exporterScrapeUp: true,
            cpuPct: 11.2,
            memPct: 43.7,
            childSAs: [this.mockChildSa(now, '-live')],
        };
    }

    private mockChildSa(now: number, idSuffix: string): ChildSaSnapshotDto {
        return {
            childId: `ch${idSuffix}`,
            childName: 'net-net-1',
            state: 'INSTALLED',
            establishedAt: now - 22 * 60 * 1000,
            rekeyAt: now + 22 * 60 * 1000,
            lastInboundSeconds: 0.4,
            lastOutboundSeconds: 0.6,
            bytesIn: 9_823_410,
            bytesOut: 4_217_882,
            localTrafficSelectors: ['10.0.0.0/16'],
            remoteTrafficSelectors: ['10.19.140.0/24', '10.19.141.0/24'],
        };
    }

    private mockMetrics(window: MetricsWindow): IpsecTunnelerMetricsDto {
        const now = Date.now();
        const interval = window === '7d' ? '15m' : window === '24h' ? '1m' : '30s';
        const stepMs = window === '7d' ? 15 * 60_000 : window === '24h' ? 60_000 : 30_000;
        const points = window === '7d' ? 96 : window === '24h' ? 120 : 120;
        const start = now - stepMs * points;
        const rxTx: RxTxPoint[] = [];
        const system: SystemPoint[] = [];
        const lastIn: ScalarPoint[] = [];
        const lastOut: ScalarPoint[] = [];
        const tunnelState: TunnelStatePoint[] = [];
        // Synthesize one downtime window mid-series so the UI dev path actually exercises
        // the red-band rendering and the rekey-vs-outage discriminator without needing a
        // live broken bridge to look at.
        const outageStart = Math.floor(points * 0.4);
        const outageEnd = Math.floor(points * 0.45);
        for (let i = 0; i < points; i++) {
            const t = start + i * stepMs;
            const phase = i / points;
            const inOutage = i >= outageStart && i <= outageEnd;
            rxTx.push({
                t,
                rx: inOutage ? null : Math.round(120_000 + 80_000 * Math.sin(phase * 8) + Math.random() * 12_000),
                tx: inOutage ? null : Math.round(80_000 + 60_000 * Math.sin(phase * 6) + Math.random() * 10_000),
            });
            system.push({ t, cpuPct: 10 + 4 * Math.sin(phase * 4), memPct: 42 + 2 * Math.sin(phase * 2) });
            lastIn.push({ t, v: Math.random() < 0.95 ? Math.random() * 2 : 12 });
            lastOut.push({ t, v: Math.random() < 0.95 ? Math.random() * 2 : 9 });
            tunnelState.push({
                t,
                ikeUp: !inOutage,
                ikesConnected: inOutage ? 0 : 1,
                // sa_established_second is "elapsed seconds since the CHILD_SA came up"
                // and grows monotonically until rekey resets it to 0. Mock value grows
                // with the bucket index so the parser's "DECREASE = rekey" heuristic
                // sees no spurious events in steady state; outage buckets emit nothing
                // for the child (it's torn down), and the first post-outage bucket
                // resets to a small value (recovery rebuild signature).
                perChildEstablishedSec: inOutage
                    ? {}
                    : { '1': i < outageStart ? (i * stepMs) / 1000 : ((i - outageEnd) * stepMs) / 1000 },
            });
        }
        const downtimeRanges: TunnelDowntimeRange[] =
            outageStart < outageEnd ? [{ start: start + outageStart * stepMs, end: start + outageEnd * stepMs }] : [];
        // The post-outage `perChildEstablishedSec` reset above already triggers the
        // outage-recovery rebuild marker via the parser. Add a handful of standalone
        // rekey markers (single child, mid-window) so the dev path also exercises the
        // ordinary-rekey rendering without the noise of every bucket being flagged.
        const rekeyEvents: RekeyEvent[] = [
            { t: start + Math.floor(points * 0.15) * stepMs, childIds: ['1'] },
            { t: start + Math.floor(points * 0.3) * stepMs, childIds: ['1'] },
            { t: start + (outageEnd + 1) * stepMs, childIds: ['1', '2'] }, // recovery rebuild
            { t: start + Math.floor(points * 0.7) * stepMs, childIds: ['2'] },
            { t: start + Math.floor(points * 0.85) * stepMs, childIds: ['1'] },
        ];
        return {
            window,
            interval,
            generatedAt: now,
            series: { rxTx, system, lastInboundSeconds: lastIn, lastOutboundSeconds: lastOut, tunnelState },
            downtimeRanges,
            rekeyEvents,
        };
    }

    private mockEvents(kind: string | null): IpsecTunnelerEventDto[] {
        const now = Date.now();
        const all: IpsecTunnelerEventDto[] = [
            {
                t: now - 2 * 60_000,
                severity: 'INFO',
                kind: 'ziti-router',
                message: '(4571) INFO tunnel-cbs:ziti_hosting.c:751 on_hosted_client_connect() ...',
            },
            {
                t: now - 8 * 60_000,
                severity: 'INFO',
                kind: 'kernel-audit',
                message: 'kernel: audit: op=SAD-add src=3.237.76.3 dst=10.19.215.109 res=1',
            },
            {
                t: now - 41 * 60_000,
                severity: 'WARN',
                kind: 'ziti-router',
                message: '(4571) WARN tunnel-cbs:ziti_hosting.c:822 hosted_service[LocalToRemote] connect failure',
            },
            {
                t: now - 67 * 60_000,
                severity: 'INFO',
                kind: 'charon',
                message: '05[IKE] <nf-bridge|6> CHILD_SA nf-bridge{7} established with SPIs cbcb47fc_i ce50af7b_o',
            },
            {
                t: now - 3 * 60 * 60_000,
                severity: 'INFO',
                kind: 'kernel-audit',
                message: 'kernel: audit: op=SAD-delete src=3.237.76.3 dst=10.19.215.109 res=1',
            },
        ];
        return kind ? all.filter((e) => e.kind === kind) : all;
    }
}
